import useCloseWhenMissing, { HOME, useCloseUnlessOrgNode } from '../hooks/useCloseWhenMissing'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { LuUsers } from 'react-icons/lu'
import { useModalClose } from '../hooks/useModalClose'
import useAccessGrants from '../models/useAccessGrants'
import useServices from '../models/useServices'
import useNode from '../models/useNode'
import SharedAccessGrants from './SharedAccessGrants'
import { panelServices, pendingServiceKey, unionKeys } from '../utilities/sharedAccess'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import IconAgent from './IconAgent'
import Modal from './Modal'

type Props = {
  inline?: boolean
}

export default function Permissions({ inline }: Readonly<Props>) {
  const { serviceName, nodeId: nodeIdFilter } = useParams<{ serviceName: string; nodeId: string }>()
  // ⚠ An id in the path that is not an object of the active org sends the reader home (app#475).
  useCloseUnlessOrgNode(nodeIdFilter)
  const { services } = useServices()
  const { nodes } = useNode()
  // ⚠ The same for a SERVICE CODE in the path (`/permissions/<code>`, the link of the
  // access-request mail) that is not a service of a node of the active org (app#477). The page
  // said "Service not found" before.
  useCloseWhenMissing({
    id: serviceName,
    found: !!serviceName && services.some(s => s.name.toLowerCase() === serviceName.toLowerCase()),
    loadedOrgId: useNode(state => state.loadedOrgId),
    orgOf: useServices(state => state.orgOfCode),
    to: HOME,
  })
  const { get: getGrants, subscribe } = useAccessGrants()
  const { open } = useModalClose('/', inline)
  const navigate = useNavigate()
  const location = useLocation()
  const handleClose = () => {
    if (inline) return
    if (location.key === 'default') navigate('/')
    else navigate(-1)
  }

  const nodeForFilter = nodeIdFilter ? nodes.find(n => n.id === nodeIdFilter) : undefined

  // ⚠ One STRING, not the grants array: the effect below fetches the grants, and a memo that
  // depended on the array would change at each fetch and run the effect again with no end.
  const pendingKey = useAccessGrants(state => pendingServiceKey(state.grants))
  // ⚠ Every id that was pending since this panel opened for this service, not only the ids pending
  // now. An answer takes a request out of "pending" at once, and its service must stay on the
  // panel: the reader has to see the result, and "Accept after all" is the only way back from a
  // wrong Reject.
  const [seen, setSeen] = useState({ serviceName, key: pendingKey })
  const seenKey = seen.serviceName === serviceName ? unionKeys(seen.key, pendingKey) : pendingKey
  useEffect(() => {
    setSeen(prev => (prev.serviceName === serviceName && prev.key === seenKey ? prev : { serviceName, key: seenKey }))
  }, [serviceName, seenKey])
  // The panel for one service also shows the other services of its node that have a pending
  // request (app#465): see `panelServices`.
  const filteredServices = useMemo(
    () => panelServices(services, seenKey, serviceName, nodeIdFilter),
    [services, seenKey, serviceName, nodeIdFilter]
  )

  // Initial fetch + realtime subscription. The FETCH is deliberately unfiltered
  // even for a single service: get(serviceId) narrows the shared store to that
  // service's rows, starving every other reader of it — visiting this modal for
  // one service used to leave the node page's grants section blank. Filtering
  // per service is the renderer's job, and RLS already scopes rows to services
  // the user owns.
  // ⚠ The SUBSCRIPTION is unfiltered too (app#465). The panel for one service also shows the
  // other pending requests of its node, so a request that arrives on another service must reach it.
  useEffect(() => {
    getGrants()
    return subscribe()
  }, [getGrants, subscribe])

  return (
    <Modal
      open={open}
      inline={inline}
      maxWidth="sm"
      onClose={inline ? undefined : handleClose}
      color="secondary.main"
      icon={
        <Box
          sx={{
            width: 54,
            height: 54,
            borderRadius: '50%',
            bgcolor: 'secondary.main',
            color: 'background.default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 2,
            borderColor: 'background.default',
          }}
        >
          <LuUsers size={28} />
        </Box>
      }
    >
      <DialogContent>
        <Stack spacing={3}>
          <Stack direction="row" sx={{
            alignItems: 'center'
          }}>
            <Typography variant="subtitle1" color="secondary" sx={{
              flexGrow: 1
            }}>
              Shared access
            </Typography>
            {nodeForFilter && (
              <Tooltip title="Node name" placement="top" arrow>
                <Stack direction="row" spacing={1} sx={{
                  alignItems: 'center'
                }}>
                  <Typography variant="code">{nodeForFilter.name}</Typography>
                  <IconAgent width={20} height={20} />
                </Stack>
              </Tooltip>
            )}
          </Stack>

          {/* A deep link (the router's access-request email, {AccessGrantURL}/
              {service.Name}) opens this for ONE service, and then the service
              is the subject of the page rather than one heading among several —
              so it is titled a level up. */}
          <SharedAccessGrants
            services={filteredServices}
            titleVariant={serviceName ? 'h5' : 'h6'}
            empty={serviceName ? `Service "${serviceName}" not found.` : 'No services with access control.'}
          />
        </Stack>
      </DialogContent>
    </Modal>
  )
}
